import { Box, Typography, useTheme } from "@mui/material";

interface LogoProps {
  size?: number;
  showWordmark?: boolean;
  /**
   * "color" — full treatment: graticule + amber pulse on a transparent ground.
   * "mono"  — single-color, inherits `currentColor` for ghost contexts
   *           (active nav rows, monochrome marketing surfaces).
   */
  variant?: "color" | "mono";
  /**
   * When true, the pulse line breathes at ~0.6 Hz — the same rhythm as
   * the streaming caret. Wired to `chatStore.isStreaming` from the
   * sidebar so the brand mark literally lights up while the agent is
   * working. The breath is opacity-only — no glow, no gradient — to
   * stay within the brand book's "line drawing" rule.
   */
  pulseEmber?: boolean;
}

// ── Brand tokens ────────────────────────────────────────────────────────────
// Locked to the design handoff (docs/design_handoff_logo/README.md). The
// app theme exposes `editorial.main` at #d4a05c which is a softer amber —
// the brand mark itself uses the canonical #F5A524 so it matches the
// favicon, OG image, plugin icons, and marketing surfaces verbatim.
const BRAND_ACCENT = "#F5A524";
const BRAND_GRATICULE = "#3A424E";

/**
 * Circuit Forge brand mark — "Pulse".
 *
 * A single square pulse waveform crossing a center-cross graticule.
 * Reads as: a measurement instrument, an event captured, a signal
 * detected. Engineering-grade, technical, calm.
 *
 * Geometry is locked to the design handoff (1024×1024 viewBox):
 *   • Graticule: horizontal y=512 spanning 160→864, vertical x=512
 *     spanning 200→824, stroke 2 at 50% opacity.
 *   • Pulse: M 200 640 L 380 640 L 380 360 L 644 360 L 644 640 L 824 640,
 *     stroke 40, round caps + round joins. Stroke ratio is 1:20
 *     (graticule:pulse) — do not change.
 *
 * Size-tiered detail:
 *   ≥22px — full treatment: graticule + pulse
 *    <22px — pulse only (favicon mode); graticule reads as noise.
 *
 * Don'ts (per the brand book):
 *   • No gradients, drop shadows, or glow on the pulse path.
 *   • No recolor outside the approved palettes.
 *   • Don't drop the graticule on full-size variants.
 */
export function Logo({
  size = 24,
  showWordmark = true,
  variant = "color",
  pulseEmber = false,
}: LogoProps) {
  const theme = useTheme();

  const isMono = variant === "mono";
  const showGraticule = size >= 22;

  // In mono mode both layers collapse to currentColor; the graticule is
  // held back via opacity (the same pattern the brand SVGs use for
  // mono-paper / pure-mono palettes).
  const accentColor = isMono ? "currentColor" : BRAND_ACCENT;
  const graticuleColor = isMono ? "currentColor" : BRAND_GRATICULE;

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.05 }}>
      <Box
        component="svg"
        viewBox="0 0 1024 1024"
        sx={{
          width: size,
          height: size,
          flexShrink: 0,
          display: "block",
          // The pulse "breath" — opacity only, no glow, per brand book.
          // Wrapped on the SVG so the wordmark dot doesn't pulse with it.
          ...(pulseEmber && {
            animation: "cf-pulse-breath 1.6s ease-in-out infinite",
            "@keyframes cf-pulse-breath": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.65 },
            },
          }),
        }}
        aria-label="Circuit Forge"
        role="img"
      >
        {/* ── Graticule (background detail) ──
            Two thin lines forming a center cross. Communicates
            "instrument" + "calm precision". Suppressed below 22px
            because at favicon sizes it reads as noise. */}
        {showGraticule && (
          <g
            stroke={graticuleColor}
            strokeWidth="2"
            opacity="0.5"
            fill="none"
          >
            <line x1="160" y1="512" x2="864" y2="512" />
            <line x1="512" y1="200" x2="512" y2="824" />
          </g>
        )}

        {/* ── Pulse (primary shape) ──
            Single square pulse: low rail → step up → high rail →
            step down → low rail. Round caps + round joins are
            load-bearing — they soften the corners enough that the
            mark reads as "rendered UI" rather than "raw oscilloscope
            trace". */}
        <path
          d="M 200 640 L 380 640 L 380 360 L 644 360 L 644 640 L 824 640"
          fill="none"
          stroke={accentColor}
          // At favicon sizes (<22px the graticule is gone) the pulse
          // gets a slightly thicker stroke so it holds up against the
          // browser tab background. Matches the favicon-ink.svg ratio.
          strokeWidth={showGraticule ? 40 : 80}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Box>

      {showWordmark && (
        <Box sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.3 }}>
          <Typography
            component="span"
            sx={{
              fontFamily: theme.fonts.mono,
              color: "text.primary",
              fontWeight: 500,
              fontSize: size >= 36 ? "1rem" : "0.82rem",
              letterSpacing: "-0.01em",
              lineHeight: 1,
            }}
          >
            circuit
          </Typography>
          {/* Brand-spec dot separator — accent-colored, sits between the
              two halves of the wordmark. The dot is the only place
              outside the mark itself where the amber appears in the
              header lockup. */}
          <Typography
            component="span"
            aria-hidden="true"
            sx={{
              color: BRAND_ACCENT,
              fontWeight: 700,
              fontSize: size >= 36 ? "1.1rem" : "0.9rem",
              lineHeight: 1,
              userSelect: "none",
              transform: "translateY(-0.05em)",
            }}
          >
            ·
          </Typography>
          <Typography
            component="span"
            sx={{
              fontFamily: theme.fonts.mono,
              color: "text.primary",
              fontWeight: 700,
              fontSize: size >= 36 ? "1rem" : "0.82rem",
              letterSpacing: "-0.01em",
              lineHeight: 1,
            }}
          >
            forge
          </Typography>
        </Box>
      )}
    </Box>
  );
}
